import { dayMonth } from "./thisWeekBoard";
import { waitDays, waitWords, type Flag, type QueueRow } from "./reviewQueue";
import { NOT_LOGGED, type SheetSetRow } from "./reviewSheet";

/* 🔴 W118 — THE SESSION OVERVIEW WINDOW'S WORDS (William, 2026-10-01 ~00:10: *"it has most info left and then all the right
 * side is empty. it could be half the horizontal size … and a little more tidy man.. idk maybe containers for each
 * exercise? do what it takes so it looks amazing and not just a bunch of text"*). The window is what a coach glances at
 * before reviewing, so it says what the client DID, tidily: a head (who · the day, the workout, when it finished and how long
 * it has waited), then one panel per exercise — the name with its written dose, then its sets written beside done.
 *
 * Every word here is DERIVED from the same sheet and queue row the review page draws (`reviewSheet`, `reviewQueue`), so the
 * window and the review can never disagree about a set. Nothing here reads the database. */

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const p2 = (n: number) => String(n).padStart(2, "0");

export type OverviewHead = {
  /** `Dana Sample · Tue 29 Sep` — who, and the day it was trained (the finish's day, in the page's zone). */
  readonly eyebrow: string;
  /** The workout's name as the coach wrote it; `Workout` when the day carries none. */
  readonly title: string;
  /** `finished 19:16 · 2 days waiting` — the wait half only when it has waited a day or more. */
  readonly caption: string;
};

export function overviewHead(row: Pick<QueueRow, "name" | "workout" | "finish">, now: number): OverviewHead {
  const d = new Date(row.finish);
  const key = `${String(d.getFullYear())}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
  const days = waitDays(row.finish, now);
  const finished = `finished ${p2(d.getHours())}:${p2(d.getMinutes())}`;
  return {
    eyebrow: `${row.name} · ${WEEKDAY[d.getDay()] ?? ""} ${dayMonth(key)}`,
    title: row.workout ?? "Workout",
    caption: days === 0 ? finished : `${finished} · ${waitWords(days)}`,
  };
}

/**
 * An exercise's WRITTEN dose, on one line beside its name: `3 × 5 @ 60 kg` when every working set was written the same,
 * `4 sets` when they differ (the table under it says each), null when nothing was written (a record read without a plan).
 * Ramps are not the dose — they are warm-ups, and the table shows them greyed.
 */
export function writtenDose(sets: readonly SheetSetRow[]): string | null {
  const work = sets.filter((s) => s.state !== "ramp" && s.written !== NOT_LOGGED);
  if (work.length === 0) return null;
  const first = work[0]?.written ?? "";
  if (work.every((s) => s.written === first)) return `${String(work.length)} × ${first}`;
  return `${String(work.length)} sets`;
}

/** The exercises this day set a PR on — by the queue's own flag (`flagsOf`), so the window and the queue agree. */
export function prNames(flags: readonly Flag[]): ReadonlySet<string> {
  return new Set(flags.flatMap((f) => (f.kind === "pr" ? [f.exercise] : [])));
}
